/**
 * Component: SpeechLanguageSelector
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R13
 *
 * Shows which language speech recognition is listening for, and lets the
 * user change it.
 *
 * SPEECH-V1-R13: the active language must be visible at the point of
 *                capture, not buried in a settings page - a user who does
 *                not know which language the recogniser expects has no way
 *                to explain a bad transcript. So this renders as a always-on
 *                label beside the microphone rather than a menu that only
 *                reveals the current state once opened.
 *
 * Deliberately presentational: resolution and persistence live in
 * useSpeechLanguage, so the same control can be reused by any surface that
 * gains a microphone without duplicating the storage rules.
 *
 * Accessibility: WCAG 2.1 AA - the trigger carries an explicit aria-label
 * naming the active language, since the visible text alone ("FranÃ§ais") does
 * not convey what the control does.
 *
 * Risk Links: R8
 * FMEA Links: FM4
 */

import type React from 'react';
import { Languages } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SpeechLanguageOption } from '@/hooks/use-speech-language';

export interface SpeechLanguageSelectorProps {
  /** Active BCP-47 language tag. */
  value: string;
  /** Languages offered for selection. */
  options: SpeechLanguageOption[];
  /** Called with the newly selected BCP-47 tag. */
  onChange: (code: string) => void;
  /** Disable while a session is live - changing mid-session would not apply. */
  disabled?: boolean;
  /** Custom class name for the trigger. */
  className?: string;
}

/**
 * Compact language control for the speech input affordance.
 */
export function SpeechLanguageSelector({
  value,
  options,
  onChange,
  disabled = false,
  className = '',
}: SpeechLanguageSelectorProps): React.ReactElement | null {
  // Nothing meaningful to choose between: render nothing rather than a
  // one-item dropdown that implies a choice the user does not have.
  if (options.length < 2) {
    return null;
  }

  const activeLabel =
    options.find((o) => o.code === value)?.label ?? value;

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={`h-9 w-auto gap-1.5 px-2 text-xs text-muted-foreground ${className}`}
        aria-label={`Speech recognition language: ${activeLabel}. Change language`}
        title={`Listening in ${activeLabel}`}
      >
        <Languages className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.code} value={option.code} className="text-sm">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default SpeechLanguageSelector;
